// src/pages/Unauthorized.jsx
import AppShell from "../layout/AppShell";
import { useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import "./Unauthorized.css";

const SS = sessionStorage;
const K = { role: "tb_role", roleRaw: "tb_role_raw", name: "tb_name", email: "tb_email" };

function normalizeRole(raw) {
    if (!raw) return "";
    const x = String(raw).toLowerCase().replace(/\s+/g, " ").trim();
    // FTE Nominator MUST be checked before bare "nominator" — it also contains the word "nominator"
    if ((x.includes("fte") && x.includes("nominator")) || x === "fte nominator") return "FTE Nominator";
    if (x.includes("nominator")) return "Nominator";
    if (x.includes("employee supplier admin") || x.includes("supplier admin")) return "Employee Supplier Admin";
    if (x.includes("employee sponsor")) return "Employee Sponsor";
    if (x.includes("dtv ops admin") || x.includes("ops admin")) return "DTV OPS Admin";
    if (x.includes("cp channel delegate") || (x.includes("cp") && x.includes("delegate")) || x === "cp delegate")
        return "CP Channel Delegate";
    return ""; // return empty when not recognized (so we can show raw separately)
}

/** Map an app pathname to Sidebar activeKey */
function routeToKey(pathname = "") {
    const p = String(pathname || "").toLowerCase();
    if (p.startsWith("/approvals")) return "approvals";
    if (p.startsWith("/manage-requests")) return "manage";
    if (p.startsWith("/onboarding")) return "onboarding";
    if (p.startsWith("/dashboard")) return "overview";
    return "overview";
}

export default function Unauthorized(props) {
    const nav = useNavigate();
    const loc = useLocation();
    const { instance, accounts } = useMsal();

    // Prefer RoleGuard's state (so we can display the role that was rejected)
    const locState = loc?.state || {};
    const fromProp = props?.from ?? locState.from;
    const allowedProp = props?.allowed ?? locState.allowed;

    // 🔑 derive the correct sidebar key from the original path (fallback to current just in case)
    const activeKey = routeToKey(fromProp || loc?.pathname);

    // Role/name/email
    // Priority order:
    //   1. props.role       — set directly by OnboardingNewGate with the current role
    //   2. locState.role    — the CURRENT normalized role passed by RoleGuard on redirect
    //   3. locState.roleRaw — fallback (may be stale SSO raw value, avoid if possible)
    //   4. tb_role          — live sessionStorage value (reflects latest role switch)
    //   5. tb_role_raw      — original SSO raw string (last resort, often stale)
    const roleRaw =
        props?.role ??
        locState.role ??                                 // ⬅️ current role from RoleGuard
        locState.roleRaw ??                              // ⬅️ fallback (may be stale)
        SS.getItem(K.role) ??                            // ⬅️ live tb_role (always current)
        SS.getItem(K.roleRaw) ??
        (accounts?.[0]?.idTokenClaims?.roles?.[0] ?? "") ??
        "";

    const normalized = normalizeRole(roleRaw);
    const roleForDisplay = normalized || roleRaw || "";
    const hasRole = !!(roleForDisplay && String(roleForDisplay).trim());

    const name = SS.getItem(K.name) || "";
    const email = SS.getItem(K.email) || "";

    const candidateReturn = fromProp || "/dashboard";
    const returnTo = candidateReturn === "/unauthorized" ? "/dashboard" : candidateReturn;

    const allowedArray =
        Array.isArray(allowedProp) ? allowedProp : (typeof allowedProp === "string" && allowedProp ? [allowedProp] : []);
    const allowedList = (allowedArray.length && !allowedArray.includes("*")) ? allowedArray.join(", ") : "";

    const contextLine = hasRole
        ? `Your current role “${roleForDisplay}” doesn’t have access to this page${allowedList ? ` (allowed: ${allowedList})` : ""}.`
        : "Your account currently doesn’t have any TeamBridge role assigned.";

    return (
        // If you want Logout visible here, consider AppShell headerHideSearch/disableSearch instead of publicMode
        <AppShell activeKey={activeKey} publicMode={true} noSidebar={!hasRole}>
            <div className="ap-page-wrap page-scroll-scope">
                <div className="una-page">
                    <div className="una-title">
                        <div className="una-title-left">
                            <span className="una-bullet" />
                            <h2>Access denied</h2>
                        </div>
                    </div>

                    <div className="card una-card">
                        <div className="una-card-head">
                            <LockIcon />
                            <div>
                                <div className="una-head-title">You don’t have permission to view this page.</div>
                                <div className="una-head-sub">
                                    {contextLine} If you believe you should have access, contact your administrator
                                    to request the appropriate role.
                                </div>
                            </div>
                        </div>

                        <div className="una-meta">
                            {name && <span className="una-chip">{name}</span>}
                            {email && <span className="una-chip">{email}</span>}
                            {hasRole ? (
                                <span className="una-chip una-chip-role">Role: {roleForDisplay}</span>
                            ) : (
                                <span className="una-chip una-chip-role">Role: (none)</span>
                            )}
                            {fromProp && <span className="una-chip">Path: {fromProp}</span>}
                            {allowedList && <span className="una-chip">Allowed: {allowedList}</span>}
                        </div>

                        <div className="onb-actions una-actions">
                            <div />
                            <div className="right">
                                <button
                                    className="btn primary"
                                    onClick={() => nav("/dashboard", { replace: true })}
                                >
                                    Go to Overview
                                </button>
                                <button
                                    className="button back"
                                    onClick={() => nav(returnTo, { replace: true })}
                                >
                                    Try again
                                </button>
                                <button
                                    className="button back"
                                    onClick={() => instance.logoutRedirect().catch(() => { })}
                                >
                                    Sign in with a different account
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="una-foot-hint">
                        Need help? Raise a request with your TeamBridge support channel.
                    </div>
                </div>
            </div>
        </AppShell>
    );
}

function LockIcon() {
    return (
        <svg viewBox="0 0 24 24" className="una-lock" width="38" height="38" aria-hidden>
            <path d="M17 9V7a5 5 0 10-10 0v2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <rect x="3.8" y="9" width="16.4" height="12" rx="2.2" ry="2.2" fill="none" stroke="currentColor" strokeWidth="2" />
            <circle cx="12" cy="15" r="2" fill="currentColor" />
        </svg>
    );
}
